// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';

type FxKind = 'spark' | 'confetti' | 'smoke';

interface FxParticle {
  kind: FxKind;
  position: any;
  velocity: any;
  rotation: any;
  spin: any;
  life: number;
  maxLife: number;
  gravity: number;
  /** 速度阻尼（火花拖尾衰减用），0 表示无阻尼。 */
  drag: number;
  /** 彩带飘落摆动相位（弧度），0 表示不摆动。 */
  flutter: number;
  baseScale: number;
  color: number;
}

interface FxBurst {
  at: number;
  x: number;
  y: number;
  z: number;
  color: number;
}

/** 胜者坦克处的光环 + 聚光柱组合（演出期间持续脉冲，末尾淡出）。 */
interface FxBeacon {
  ring: any;
  column: any;
  age: number;
}

/** 烟花/彩带调色板：与游戏卡通低多边形美术统一的亮色系。 */
const FIREWORK_COLORS = [0xff5d8f, 0xffd34f, 0x6fe07a, 0x58b6ff, 0xc07bff, 0xff8a45];
const CONFETTI_COLORS = [...FIREWORK_COLORS, 0xfff4d3];
const SMOKE_COLOR = 0x453e4c;
const GOLD = 0xffd34f;
/** 光环/聚光柱持续时长（秒），最后 1.2s 淡出。 */
const BEACON_SEC = 7;
/** 演出总时长（秒），之后不再有新粒子产生。 */
const CELEBRATE_SEC = 6;

/**
 * PvP 对局结算胜利演出系统（自包含，风格对齐 VfxSystem）：
 * - 胜者坦克上空多轮彩色烟花爆发（点粒子、重力下落、阻尼拖尾淡出）；
 * - 彩带粒子雨从天空洒落（翻转飘落、触地弹跳）；
 * - 胜者脚下金色光环脉冲 + 头顶旋转聚光柱；
 * - 败方坦克处升起暗色闷烟（圆胖粒子缓慢上浮放大）。
 *
 * 全部粒子走三块稳定 InstancedMesh 表面，材质/几何体在 dispose 统一释放。
 */
export class VictoryFX {
  private readonly particles: FxParticle[] = [];
  private readonly particlePool: FxParticle[] = [];
  private readonly pendingBursts: FxBurst[] = [];
  private readonly beacons: FxBeacon[] = [];
  private loserPositions: Array<{ x: number; z: number }> = [];
  private confettiBounds: { minX: number; maxX: number; minZ: number; maxZ: number } | null = null;

  private readonly sparkGeometry = new THREE.SphereGeometry(0.15, 8, 6);
  private readonly confettiGeometry = new THREE.BoxGeometry(0.17, 0.02, 0.24);
  private readonly smokeGeometry = new THREE.SphereGeometry(0.32, 8, 6);
  private readonly ringGeometry = new THREE.RingGeometry(0.85, 1.06, 36);
  private readonly columnGeometry = new THREE.CylinderGeometry(1.35, 0.5, 15, 20, 1, true);

  private readonly sparkInstances: any;
  private readonly confettiInstances: any;
  private readonly smokeInstances: any;
  private readonly sparkMaterial: any;
  private readonly confettiMaterial: any;
  private readonly smokeMaterial: any;
  private readonly dummy = new THREE.Object3D();
  private readonly tempColor = new THREE.Color();

  private readonly maxSparks: number;
  private readonly maxConfetti: number;
  private readonly maxSmoke: number;

  private elapsed = 0;
  private active = false;
  private confettiUntil = 0;
  private confettiTimer = 0;
  private smokeUntil = 0;
  private smokeTimer = 0;

  constructor(private readonly scene: any, private qualityScale = 1) {
    this.maxSparks = Math.max(48, Math.round(240 * qualityScale));
    this.maxConfetti = Math.max(32, Math.round(150 * qualityScale));
    this.maxSmoke = Math.max(12, Math.round(70 * qualityScale));

    this.sparkMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    this.confettiMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      toneMapped: false,
    });
    this.smokeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      toneMapped: false,
    });

    this.sparkInstances = this.createInstanceSurface(this.sparkGeometry, this.sparkMaterial, this.maxSparks);
    this.confettiInstances = this.createInstanceSurface(this.confettiGeometry, this.confettiMaterial, this.maxConfetti);
    this.smokeInstances = this.createInstanceSurface(this.smokeGeometry, this.smokeMaterial, this.maxSmoke);
    this.scene.add(this.sparkInstances, this.confettiInstances, this.smokeInstances);
  }

  /** 与 VfxSystem 相同的预编译技巧：先摆一个零缩放实例避免首帧着色器卡顿。 */
  private createInstanceSurface(geometry: any, material: any, capacity: number): any {
    const mesh = new THREE.InstancedMesh(geometry, material, capacity);
    mesh.count = capacity > 0 ? 1 : 0;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (capacity > 0) {
      mesh.setMatrixAt(0, new THREE.Matrix4().makeScale(0, 0, 0));
      mesh.setColorAt(0, this.tempColor.setHex(0xffffff));
      if (mesh.instanceColor) mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    return mesh;
  }

  /**
   * 启动整场胜利演出。
   *
   * @param winnerPositions 胜方坦克世界坐标（已下场坦克取最后位置；为空退回场地中心）。
   * @param loserPositions 败方坦克世界坐标（为空则不放闷烟）。
   */
  celebrate(winnerPositions: Array<{ x: number; z: number }>, loserPositions: Array<{ x: number; z: number }>): void {
    this.resetFx();
    this.active = true;
    this.elapsed = 0;
    const winners = winnerPositions.length > 0 ? winnerPositions : [{ x: 0, z: 0 }];

    // 多轮烟花：轮流以各胜者坦克为圆心随机偏移，整场约 4.5s 内打 9 轮。
    const rounds = 9;
    for (let r = 0; r < rounds; r += 1) {
      const base = winners[r % winners.length];
      this.pendingBursts.push({
        at: 0.15 + r * 0.5 + Math.random() * 0.22,
        x: base.x + (Math.random() - 0.5) * 6,
        y: 6.5 + Math.random() * 3,
        z: base.z + (Math.random() - 0.5) * 6,
        color: FIREWORK_COLORS[r % FIREWORK_COLORS.length],
      });
    }
    this.pendingBursts.sort((a, b) => a.at - b.at);

    // 彩带雨范围覆盖所有胜者坦克（外扩 4 格）。
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const pos of winners) {
      minX = Math.min(minX, pos.x);
      maxX = Math.max(maxX, pos.x);
      minZ = Math.min(minZ, pos.z);
      maxZ = Math.max(maxZ, pos.z);
    }
    this.confettiBounds = { minX: minX - 4, maxX: maxX + 4, minZ: minZ - 4, maxZ: maxZ + 4 };
    this.confettiUntil = 4.5;
    this.confettiTimer = 0;

    // 败方闷烟。
    this.loserPositions = loserPositions.slice();
    this.smokeUntil = loserPositions.length > 0 ? 3.2 : 0;
    this.smokeTimer = 0;

    // 胜者光环 + 聚光柱。
    for (const pos of winners) this.addBeacon(pos.x, pos.z);
  }

  update(dt: number): void {
    if (!this.active && this.particles.length === 0) return;
    this.elapsed += dt;

    // 到点的烟花逐轮爆发。
    while (this.pendingBursts.length > 0 && this.pendingBursts[0].at <= this.elapsed) {
      const burst = this.pendingBursts.shift();
      if (burst) this.spawnBurst(burst);
    }
    // 彩带雨（~20 粒/秒，持续 4.5s）。
    if (this.confettiBounds && this.elapsed < this.confettiUntil) {
      this.confettiTimer -= dt;
      while (this.confettiTimer <= 0) {
        this.confettiTimer += 0.05;
        this.spawnConfetti();
      }
    }
    // 败方闷烟（~8 粒/秒，随机挑一个败方位置）。
    if (this.elapsed < this.smokeUntil && this.loserPositions.length > 0) {
      this.smokeTimer -= dt;
      while (this.smokeTimer <= 0) {
        this.smokeTimer += 0.12;
        const base = this.loserPositions[Math.floor(Math.random() * this.loserPositions.length)];
        this.spawnSmoke(base.x, base.z);
      }
    }
    if (this.elapsed > CELEBRATE_SEC && this.particles.length === 0) this.active = false;

    this.integrateParticles(dt);
    this.updateBeacons(dt);
    this.syncInstances();
  }

  /** 释放全部资源（几何体/材质/实例表面），之后本实例不可再用。 */
  dispose(): void {
    this.resetFx();
    this.scene.remove(this.sparkInstances, this.confettiInstances, this.smokeInstances);
    this.sparkInstances.dispose?.();
    this.confettiInstances.dispose?.();
    this.smokeInstances.dispose?.();
    for (const geometry of [this.sparkGeometry, this.confettiGeometry, this.smokeGeometry, this.ringGeometry, this.columnGeometry]) {
      geometry.dispose?.();
    }
    for (const material of [this.sparkMaterial, this.confettiMaterial, this.smokeMaterial]) {
      material.dispose?.();
    }
  }

  /** 清掉演出中的动态状态（粒子/待放烟花/光环），实例表面与几何体保留复用。 */
  private resetFx(): void {
    while (this.particles.length > 0) this.releaseParticle(this.particles.length - 1);
    this.pendingBursts.length = 0;
    this.loserPositions = [];
    this.confettiBounds = null;
    this.active = false;
    this.confettiUntil = 0;
    this.smokeUntil = 0;
    for (const beacon of this.beacons) {
      this.scene.remove(beacon.ring, beacon.column);
      beacon.ring.material.dispose?.();
      beacon.column.material.dispose?.();
    }
    this.beacons.length = 0;
    this.syncInstances();
  }

  private addBeacon(x: number, z: number): void {
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: GOLD,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    const ring = new THREE.Mesh(this.ringGeometry, ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, 0.07, z);
    const columnMaterial = new THREE.MeshBasicMaterial({
      color: GOLD,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    const column = new THREE.Mesh(this.columnGeometry, columnMaterial);
    column.position.set(x, 7.5, z);
    this.scene.add(ring, column);
    this.beacons.push({ ring, column, age: 0 });
  }

  private updateBeacons(dt: number): void {
    for (let i = this.beacons.length - 1; i >= 0; i -= 1) {
      const beacon = this.beacons[i];
      beacon.age += dt;
      const fade = THREE.MathUtils.clamp((BEACON_SEC - beacon.age) / 1.2, 0, 1);
      if (fade <= 0) {
        this.scene.remove(beacon.ring, beacon.column);
        beacon.ring.material.dispose?.();
        beacon.column.material.dispose?.();
        this.beacons.splice(i, 1);
        continue;
      }
      const pulse = 0.5 + 0.5 * Math.sin(beacon.age * 4.2);
      beacon.ring.scale.setScalar(1 + pulse * 0.22);
      beacon.ring.material.opacity = (0.42 + pulse * 0.3) * fade;
      beacon.column.material.opacity = (0.12 + pulse * 0.07) * fade;
      beacon.column.rotation.y += dt * 0.6;
    }
  }

  /** 单轮烟花：球面放射火花，重力 + 阻尼形成拖尾弧线，混少量金色大星。 */
  private spawnBurst(burst: FxBurst): void {
    const count = this.scaledCount(26);
    for (let i = 0; i < count; i += 1) {
      const theta = Math.random() * Math.PI * 2;
      const cosPhi = Math.random() * 2 - 1;
      const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
      const speed = 4.6 + Math.random() * 4.2;
      const isStar = i % 6 === 0;
      this.spawnParticle(
        'spark',
        burst.x,
        burst.y,
        burst.z,
        Math.cos(theta) * sinPhi * speed,
        cosPhi * speed * 0.9 + 0.6,
        Math.sin(theta) * sinPhi * speed,
        isStar ? 0xfff3c4 : Math.random() < 0.25 ? 0xffffff : burst.color,
        1.1 + Math.random() * 0.7,
        5.6,
        1.15,
        0,
        isStar ? 1.6 : 1,
      );
    }
  }

  /** 彩带：从高空匀速飘落，正弦摆动 + 高速翻转，触地后弹跳衰减。 */
  private spawnConfetti(): void {
    const bounds = this.confettiBounds;
    if (!bounds) return;
    const x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
    const z = bounds.minZ + Math.random() * (bounds.maxZ - bounds.minZ);
    this.spawnParticle(
      'confetti',
      x,
      12 + Math.random() * 2.5,
      z,
      (Math.random() - 0.5) * 1.2,
      -2.2 - Math.random() * 1.2,
      (Math.random() - 0.5) * 1.2,
      CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      6,
      0,
      0,
      Math.random() * Math.PI * 2,
      1,
    );
  }

  /** 败方闷烟：圆胖暗色粒子缓慢上浮并放大。 */
  private spawnSmoke(x: number, z: number): void {
    this.spawnParticle(
      'smoke',
      x + (Math.random() - 0.5) * 1.2,
      0.5,
      z + (Math.random() - 0.5) * 1.2,
      (Math.random() - 0.5) * 0.6,
      1.0 + Math.random() * 0.8,
      (Math.random() - 0.5) * 0.6,
      SMOKE_COLOR,
      1.5 + Math.random() * 0.6,
      -0.4,
      0,
      0,
      0.9 + Math.random() * 0.5,
    );
  }

  private integrateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const particle = this.particles[i];
      particle.life -= dt;
      if (particle.life <= 0) {
        this.releaseParticle(i);
        continue;
      }
      particle.velocity.y -= particle.gravity * dt;
      if (particle.drag > 0) particle.velocity.multiplyScalar(Math.max(0, 1 - particle.drag * dt));
      if (particle.flutter !== 0) {
        const age = particle.maxLife - particle.life;
        particle.velocity.x += Math.sin(age * 9 + particle.flutter) * 1.6 * dt;
        particle.velocity.z += Math.cos(age * 7 + particle.flutter) * 1.6 * dt;
      }
      particle.position.addScaledVector(particle.velocity, dt);
      particle.rotation.x += particle.spin.x * dt;
      particle.rotation.y += particle.spin.y * dt;
      particle.rotation.z += particle.spin.z * dt;
      // 火花/彩带触地弹跳衰减；闷烟不受地面限制。
      if (particle.kind !== 'smoke' && particle.position.y < 0.06) {
        particle.position.y = 0.06;
        particle.velocity.y *= -0.25;
        particle.velocity.x *= 0.7;
        particle.velocity.z *= 0.7;
      }
    }
  }

  private syncInstances(): void {
    let sparkCount = 0;
    let confettiCount = 0;
    let smokeCount = 0;
    for (const particle of this.particles) {
      const normalized = Math.max(0, particle.life / particle.maxLife);
      const fadeScale = Math.min(1, normalized * 3.5);
      let scale = particle.baseScale * fadeScale;
      if (particle.kind === 'smoke') scale = particle.baseScale * Math.min(1, normalized * 4) * (0.7 + (1 - normalized) * 1.7);
      this.dummy.position.copy(particle.position);
      this.dummy.rotation.set(particle.rotation.x, particle.rotation.y, particle.rotation.z);
      this.dummy.scale.setScalar(Math.max(0.001, scale));
      this.dummy.updateMatrix();
      this.tempColor.setHex(particle.color);
      if (particle.kind === 'spark' && normalized < 0.3) this.tempColor.multiplyScalar(0.7 + normalized);
      if (particle.kind === 'spark') {
        this.sparkInstances.setMatrixAt(sparkCount, this.dummy.matrix);
        this.sparkInstances.setColorAt(sparkCount, this.tempColor);
        sparkCount += 1;
      } else if (particle.kind === 'confetti') {
        this.confettiInstances.setMatrixAt(confettiCount, this.dummy.matrix);
        this.confettiInstances.setColorAt(confettiCount, this.tempColor);
        confettiCount += 1;
      } else {
        this.smokeInstances.setMatrixAt(smokeCount, this.dummy.matrix);
        this.smokeInstances.setColorAt(smokeCount, this.tempColor);
        smokeCount += 1;
      }
    }
    this.commitInstances(this.sparkInstances, sparkCount);
    this.commitInstances(this.confettiInstances, confettiCount);
    this.commitInstances(this.smokeInstances, smokeCount);
  }

  private commitInstances(mesh: any, count: number): void {
    const countChanged = mesh.count !== count;
    mesh.count = count;
    if (count > 0 || countChanged) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }

  private scaledCount(value: number): number {
    return Math.max(1, Math.round(value * this.qualityScale));
  }

  private spawnParticle(
    kind: FxKind,
    x: number,
    y: number,
    z: number,
    velocityX: number,
    velocityY: number,
    velocityZ: number,
    color: number,
    life: number,
    gravity: number,
    drag: number,
    flutter: number,
    scale: number,
  ): void {
    const capacity = kind === 'spark' ? this.maxSparks : kind === 'confetti' ? this.maxConfetti : this.maxSmoke;
    let alive = 0;
    for (const particle of this.particles) if (particle.kind === kind) alive += 1;
    if (alive >= capacity) return;
    const particle = this.particlePool.pop() ?? this.createParticleState();
    particle.kind = kind;
    particle.position.set(x, y, z);
    particle.velocity.set(velocityX, velocityY, velocityZ);
    particle.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    particle.spin.set((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);
    particle.life = life;
    particle.maxLife = life;
    particle.gravity = gravity;
    particle.drag = drag;
    particle.flutter = flutter;
    particle.baseScale = scale * (0.7 + Math.random() * 0.6);
    particle.color = color;
    this.particles.push(particle);
  }

  private createParticleState(): FxParticle {
    return {
      kind: 'spark',
      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      rotation: new THREE.Vector3(),
      spin: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      gravity: 0,
      drag: 0,
      flutter: 0,
      baseScale: 1,
      color: 0xffffff,
    };
  }

  private releaseParticle(index: number): void {
    const particle = this.particles[index];
    const last = this.particles.pop();
    if (!particle || !last) return;
    if (index < this.particles.length) this.particles[index] = last;
    this.particlePool.push(particle);
  }
}
